import { useEffect, useRef, useState, type ReactNode } from "react";

import { useGame } from "../game/GameContext";
import type { AvailableExit, QuickAction, TakeableItem } from "../game/api";

/**
 * Issue #444: quick-actions strip rendered between the prose log and
 * the input form. The container is intentionally composable — a
 * horizontal flex strip of one-or-more chip groups — so #492
 * (inventory) and #493 (say/do toggle) can drop sibling sub-components
 * in alongside :func:`MoveControl` without rewriting this file.
 *
 * Issue #531: the previous per-direction exit chips were collapsed
 * into a single :func:`MoveControl` Move chip that opens a drop-up
 * panel listing destination room names. Keeps the strip compact in
 * rooms with many exits and surfaces where each direction leads.
 *
 * Each sub-component reads what it needs from ``useGame()`` and
 * renders nothing when it has nothing to show, so adding a group is a
 * pure additive change.
 */
export default function QuickActions() {
  return (
    <QuickActionsBar>
      <MoveControl />
      <TakeControl />
    </QuickActionsBar>
  );
}

function QuickActionsBar({ children }: { children: ReactNode }) {
  return (
    <div className="quick-actions" role="toolbar" aria-label="Quick actions">
      {children}
    </div>
  );
}

function MoveControl() {
  const { availableExits, busy, submitAction } = useGame();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const node = containerRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Hide the control entirely when there's nowhere to go — avoids
  // dangling an empty popover.
  if (!availableExits || availableExits.length === 0) return null;

  const pick = (exit: AvailableExit) => {
    setOpen(false);
    const action = `go ${exit.direction}`;
    void submitAction(action, {
      quickAction: { kind: "move", direction: exit.direction },
    });
  };

  return (
    <div
      className="quick-actions-group quick-actions-move"
      aria-label="Move"
      ref={containerRef}
    >
      {open && (
        <div className="move-popover" role="menu" aria-label="Choose destination">
          {availableExits.map((exit) => (
            <MoveOption
              key={exit.direction}
              exit={exit}
              busy={busy}
              onPick={pick}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className="quick-action-chip move-trigger icon-label"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Move"
      >
        {/* Issue #542: compass-rose glyph — matches the stroke-based
            Lucide-style vocabulary the Header buttons use. ``aria-hidden``
            so the visible "Move" text remains the accessible name. */}
        <svg
          aria-hidden="true"
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </svg>
        Move
      </button>
    </div>
  );
}

function TakeControl() {
  // Issue #545: "Take" quick-action chip — a drop-up popover listing
  // every takeable item in the current location, analogous to
  // ``MoveControl``. Unlike Move there is no typed quick_action
  // payload today: ``take <name>`` round-trips through the normal
  // classifier which already routes correctly to PlayerInventoryFacet.
  const { takeableEntities, busy, submitAction } = useGame();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const node = containerRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Hide the control entirely when there's nothing here to take —
  // avoids dangling an empty popover.
  if (!takeableEntities || takeableEntities.length === 0) return null;

  const pick = (item: TakeableItem) => {
    setOpen(false);
    void submitAction(`take ${item.name}`);
  };

  return (
    <div
      className="quick-actions-group quick-actions-take"
      aria-label="Take"
      ref={containerRef}
    >
      {open && (
        <div className="take-popover" role="menu" aria-label="Choose item to take">
          {takeableEntities.map((item) => (
            <TakeOption
              key={item.entity_id}
              item={item}
              busy={busy}
              onPick={pick}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className="quick-action-chip take-trigger icon-label"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Take"
      >
        {/* Hand / grab glyph — stroke-based to match the Move chip's
            Lucide-style vocabulary. ``aria-hidden`` so the visible
            "Take" text remains the accessible name. */}
        <svg
          aria-hidden="true"
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 11V6a2 2 0 0 0-4 0v5" />
          <path d="M14 10V4a2 2 0 0 0-4 0v6" />
          <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
          <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        </svg>
        Take
      </button>
    </div>
  );
}

function TakeOption({
  item,
  busy,
  onPick,
}: {
  item: TakeableItem;
  busy: boolean;
  onPick: (item: TakeableItem) => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="take-option"
      disabled={busy}
      onClick={() => onPick(item)}
    >
      {item.name}
    </button>
  );
}

function MoveOption({
  exit,
  busy,
  onPick,
}: {
  exit: AvailableExit;
  busy: boolean;
  onPick: (exit: AvailableExit) => void;
}) {
  // Issue #499: the option submits a typed ``quick_action`` payload
  // (via onPick) so the server bypasses the router / classifier; the
  // human-readable ``go <dir>`` action string is still sent so the
  // player log + narrator prompt match the typed-text path.
  // Issue #531: prefer the destination room name; fall back to the
  // bare direction when the target_label isn't authored.
  const label = exit.target_label
    ? `${exit.target_label} (${exit.direction})`
    : `go ${exit.direction}`;
  return (
    <button
      type="button"
      role="menuitem"
      className="move-option"
      disabled={busy}
      onClick={() => onPick(exit)}
    >
      {label}
    </button>
  );
}
